import React from 'react';
import { Box, Grid, Typography } from '@mui/material';
import {
  DashboardOutlined,
  StorefrontOutlined,
  CalendarMonthOutlined,
  MenuBookOutlined,
  NotificationsActiveOutlined,
  ExploreOutlined,
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import SectionContainer from '../../components/SectionContainer';
import RevealOnScroll from '../../components/RevealOnScroll';

const FEATURES = [
  {
    Icon: DashboardOutlined,
    title: 'Lead Management Dashboard',
    description:
      'Every inquiry in one pipeline: new → contacted → interested → enrolled. Notes, follow-ups, full student details.',
  },
  {
    Icon: StorefrontOutlined,
    title: 'Rich Institute Profile',
    description:
      'Gallery, courses, facilities, faculty, established year, success rate — all in one beautiful page.',
  },
  {
    Icon: CalendarMonthOutlined,
    title: 'Batch Calendar',
    description:
      'Manage upcoming batches with timings, seats left, faculty names, and video previews.',
  },
  {
    Icon: MenuBookOutlined,
    title: 'Course Plans',
    description:
      'Offer multiple programs with duration, mode (online/offline/hybrid), batch size, and features.',
  },
  {
    Icon: NotificationsActiveOutlined,
    title: 'Instant Lead Alerts',
    description:
      'In-app notification and web push the moment a student expresses interest. Never miss a lead.',
  },
  {
    Icon: ExploreOutlined,
    title: 'Geo-targeted Discovery',
    description:
      'Students near your institute find you first. No ad budget needed.',
  },
];

function FeaturesSection() {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const orange = theme.palette.brand.orange.main;
  const golden = theme.palette.brand.golden.main;

  const cardBg = isDark ? 'rgba(27,54,93,0.5)' : theme.palette.background.paper;
  const cardBorder = isDark
    ? '1px solid rgba(255,255,255,0.07)'
    : '1px solid rgba(27,54,93,0.06)';

  return (
    <SectionContainer
      id="features"
      eyebrow="Everything you get"
      title="A complete growth platform for your institute."
      sx={{
        background: isDark
          ? 'rgba(255,255,255,0.01)'
          : 'rgba(242,101,34,0.02)',
      }}
    >
      <Grid container spacing={{ xs: 2, md: 3 }}>
        {FEATURES.map(({ Icon, title, description }, i) => (
          <Grid item xs={12} sm={6} md={4} key={title}>
            <RevealOnScroll delay={i * 0.08}>
              <Box
                sx={{
                  p: { xs: 3, md: 3.5 },
                  borderRadius: '20px',
                  background: cardBg,
                  border: cardBorder,
                  height: '100%',
                  boxShadow: isDark
                    ? '0 4px 20px rgba(0,0,0,0.2)'
                    : '0 4px 20px rgba(27,54,93,0.06)',
                  transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: isDark
                      ? '0 8px 32px rgba(0,0,0,0.3)'
                      : '0 8px 32px rgba(27,54,93,0.12)',
                  },
                }}
              >
                <Box
                  sx={{
                    width: 44,
                    height: 44,
                    borderRadius: '12px',
                    background: `linear-gradient(135deg, ${orange}, ${golden})`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mb: 2,
                  }}
                >
                  <Icon sx={{ color: '#fff', fontSize: 22 }} />
                </Box>
                <Typography
                  variant="subtitle1"
                  sx={{ fontWeight: 700, color: 'text.primary', mb: 0.75, lineHeight: 1.3 }}
                >
                  {title}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.65 }}>
                  {description}
                </Typography>
              </Box>
            </RevealOnScroll>
          </Grid>
        ))}
      </Grid>
    </SectionContainer>
  );
}

export default FeaturesSection;
